import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { Auth } from '@core/services/auth';
import { Permissions } from '@core/services/permissions';
import { Theme } from '@core/services/theme';
import { NgIcon } from '@ng-icons/core';
import { MenuItem } from 'primeng/api';
import { DrawerModule } from 'primeng/drawer';
import { Menu } from 'primeng/menu';
import { RouteNavbar } from '../../interfaces/routes-navbar';
import { GlobalSearch } from '../global-search/global-search';
import { SidebarDropdown } from '../sidebar-dropdown/sidebar-dropdown';
import { Tabs } from '../tabs/tabs';

@Component({
  selector: 'app-auth-sidebar',
  imports: [Tabs, SidebarDropdown, NgIcon, DrawerModule, Menu, GlobalSearch],
  templateUrl: './auth-sidebar.html',
  styleUrl: './auth-sidebar.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthSidebar {
  private router = inject(Router);
  private theme = inject(Theme);
  private auth = inject(Auth);
  private permissions = inject(Permissions);

  currentRoute = signal<string>(this.router.url);
  darkMode = computed(() => this.theme.darkMode());
  visible = signal<boolean>(false);
  routes = signal<RouteNavbar[]>([]);

  constructor() {
    // Reaccionar de forma reactiva cuando el perfil de usuario esté disponible
    effect(() => {
      const profile = this.auth.profile();
      if (profile) {
        this.loadRoutes(profile.role);
      } else {
        this.routes.set([]);
      }
    });
  }

  async loadRoutes(role: string) {
    try {
      const visiblePermissions = await this.permissions.getVisibleRoutes(role);

      const mapPermission = (
        p: (typeof visiblePermissions)[0]
      ): RouteNavbar => {
        let path = p.route || '';
        if (path === '') {
          path = '/';
        } else if (!path.startsWith('/')) {
          path = '/' + path;
        }
        return {
          title: p.title!,
          path: path,
          iconFilled: p.icon_filled || 'matHome',
          iconOutline: p.icon_outline || 'matHomeOutline',
          roles: p.roles,
          isDropdown: p.is_dropdown ?? false,
          children: p.children?.map(mapPermission),
        };
      };

      const routesData: RouteNavbar[] = visiblePermissions.map(mapPermission);
      this.routes.set(routesData);
    } catch (error) {
      console.error('❌ Error loading routes:', error);
    }
  }

  menuItems = computed<MenuItem[]>(() => [
    {
      label: this.darkMode() ? 'Modo Claro' : 'Modo Oscuro',
      icon: this.darkMode() ? 'matLightModeOutline' : 'matDarkModeOutline',
      command: () => this.toggleThemeMode(),
    },
    {
      label: 'Cerrar Sesión',
      icon: 'matLogoutOutline',
      command: () => this.signOut(),
    },
  ]);

  toggle(): void {
    this.visible.set(!this.visible());
  }

  toggleThemeMode() {
    this.theme.toggleThemeMode();
  }

  async signOut() {
    this.visible.set(false);
    await this.auth.signOut();
    await this.router.navigateByUrl('/login');
  }
}
